// ─── Chin Risk Control Hook ────────────────────────────────
// Bridges the ChinMonitor workspace with live cTrader data:
//   - Fetches open positions periodically (every 8s)
//   - Reads spread from engine state (real-time via stream)
//   - Evaluates risk rules (1-4) and exposes results
//
// Rule 4 (After Hours): Integrated into evaluateRisk().
// When triggered (23:00 GMT+7 daily), calls onDailyReset to
// turn off all running engines. Tracked by afterHoursResetFiredDate
// to prevent duplicate resets.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { getAccountData } from '@/lib/ctrader-gateway-client';
import {
  evaluateRisk,
  calcSpreadPips,
  getAfterHoursDate,
  type RiskPosition,
  type RiskSpreadInfo,
  type RiskSymbolConfig,
  type RiskEvaluation,
} from '@/lib/chin-risk-controller';
import type { ChinBotSnapshot } from '@/lib/chin-bot-engine';

// ─── Types ──────────────────────────────────────────────────

interface UseChinRiskControlParams {
  accountId: string;
  engineState: ChinBotSnapshot | null;
  symbolConfigs: Record<number, RiskSymbolConfig>;
  activeSymbolIds: number[];
  /** Called when risk rules demand a full workspace reset (Rule 2 or 3 violated).
   *  The callback should set all symbol directions to null and persist to Supabase. */
  onEmergencyReset: (reason: string) => void;
  /** Called when Rule 4 daily reset fires (23:00 GMT+7).
   *  Should abort all running order loops/off engines and set directions to null,
   *  but preserve all other params (conditionPrice, maxOrders, volume, statusMap). */
  onDailyReset: (reason: string) => void;
  /** When false, Rule 4 (After Hours daily shutdown) is disabled entirely. */
  rule4Enabled: boolean;
}

interface UseChinRiskControlReturn {
  /** Latest risk evaluation for all symbols */
  evaluation: RiskEvaluation | null;
  /** Whether we're currently fetching positions */
  isLoading: boolean;
  /** Manually refresh positions now */
  refreshPositions: () => void;
  /** Check if a symbol can place an order right now */
  canPlaceOrder: (symbolId: number) => { allowed: boolean; reason: string };
  /** All currently open positions */
  positions: RiskPosition[];
  /** ISO date string of the last daily reset that fired (empty if none today) */
  dailyResetDate: string;
  /** UTC timestamp (ms) when the last daily reset fired (0 if none today) */
  dailyResetTimestamp: number;
}

// ─── Constants ──────────────────────────────────────────────

const POSITION_POLL_INTERVAL = 300000; // 5-min safety re-sync (spread/price is realtime via stream)

// ─── Hook ───────────────────────────────────────────────────

export function useChinRiskControl({
  accountId,
  engineState,
  symbolConfigs,
  activeSymbolIds,
  onEmergencyReset,
  onDailyReset,
  rule4Enabled,
}: UseChinRiskControlParams): UseChinRiskControlReturn {
  const [positions, setPositions] = useState<RiskPosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [evaluation, setEvaluation] = useState<RiskEvaluation | null>(null);

  // Track the last reset reason to prevent repeated resets for the same violation
  const lastResetReasonRef = useRef<string | null>(null);
  // Track whether positions have actually changed since the last reset
  const lastPositionSnapshotRef = useRef<string>('');

  // ─── Rule 4: After-Hours daily reset tracking ───────────
  // Persisted across renders so we don't fire twice in one day.
  const afterHoursResetFiredDateRef = useRef<string>('');
  // State mirror — triggers UI re-render for countdown display
  const [dailyResetDate, setDailyResetDate] = useState<string>('');
  const [dailyResetTimestamp, setDailyResetTimestamp] = useState<number>(0);

  // Store onDailyReset in a ref so runEvaluation can use latest callback
  // without depending on it (which changes on every tick due to closure over React state).
  const onDailyResetRef = useRef(onDailyReset);
  onDailyResetRef.current = onDailyReset;

  // ─── Fetch open positions ────────────────────────────────

  const fetchPositions = useCallback(async () => {
    if (!accountId) return;
    setIsLoading(true);
    try {
      const numId = parseInt(accountId, 10);
      if (isNaN(numId)) return;

      const data = await getAccountData(numId);

      if (data?.success && data.reconcileRes) {
        const raw = data.reconcileRes;
        const posArray = Array.isArray(raw.position)
          ? (raw.position as Array<Record<string, unknown>>)
          : [];

        const mapped: RiskPosition[] = posArray.map((p) => {
          const td = (p.tradeData as Record<string, unknown>) || {};
          const volRaw = typeof td.volume === 'number' ? td.volume : 0;
          const volLots = volRaw / 10_000_000;
          const sideRaw = typeof td.tradeSide === 'number' ? td.tradeSide : 1;
          const side = sideRaw === 2 ? 'SELL' : 'BUY';
          const sid = typeof td.symbolId === 'number' ? td.symbolId : 0;

          return {
            positionId: typeof p.positionId === 'number' ? p.positionId : 0,
            symbolId: sid,
            symbolName: `Symbol #${sid}`,
            tradeSide: side as 'BUY' | 'SELL',
            volume: volLots,
          };
        });

        setPositions(mapped);
      }
    } catch (err) {
      console.warn('[ChinRiskControl] Failed to fetch positions:', err);
    } finally {
      setIsLoading(false);
    }
  }, [accountId]);

  // ─── Build spread info from engine state ─────────────────

  const buildSpreadInfo = useCallback((): Record<number, RiskSpreadInfo> => {
    const info: Record<number, RiskSpreadInfo> = {};
    if (!engineState) return info;

    for (const symId of activeSymbolIds) {
      const buf = engineState.symbols[symId];
      if (!buf) continue;

      const spreadPips = calcSpreadPips(
        buf.currentBid,
        buf.currentAsk,
        buf.symbolName,
        buf.pipPosition ?? null,
      );

      info[symId] = {
        symbolId: symId,
        symbolName: buf.symbolName,
        bid: buf.currentBid,
        ask: buf.currentAsk,
        spreadPips,
      };
    }

    return info;
  }, [engineState, activeSymbolIds]);

  // ─── Run risk evaluation ─────────────────────────────────

  const runEvaluation = useCallback(() => {
    const spreadInfo = buildSpreadInfo();

    // Pass the after-hours reset tracker into evaluateRisk
    const result = evaluateRisk(
      positions,
      spreadInfo,
      symbolConfigs,
      activeSymbolIds,
      afterHoursResetFiredDateRef.current,
      rule4Enabled,
    );

    // Only update state if evaluation actually changed — prevents blocking
    // React transitions (e.g. navigation) with continuous low-value updates.
    setEvaluation((prev) => {
      if (prev && prev.needsFullReset === result.needsFullReset && prev.resetReason === result.resetReason) {
        // Quick path: compare position counts and blocked-by status
        const prevBlocked = Object.values(prev.checks).filter((c) => !c.allowed).length;
        const nextBlocked = Object.values(result.checks).filter((c) => !c.allowed).length;
        if (prevBlocked === nextBlocked) {
          // Deep check a few key fields
          const prevKeys = Object.keys(prev.checks).sort().join(',');
          const nextKeys = Object.keys(result.checks).sort().join(',');
          if (prevKeys === nextKeys) {
            // Most likely unchanged — skip re-render
            return prev;
          }
        }
      }
      return result;
    });

    // ─── Emergency Reset Detection (Rule 2 or 3) ──────────
    if (result.needsFullReset && result.resetReason) {
      // Determine if this is Rule 4 (after-hours) or Rule 2/3 (emergency)
      const isAfterHours = result.checks[activeSymbolIds[0]]?.blockedBy === 'afterHours';

      if (isAfterHours) {
        // ─── Rule 4: After-Hours Daily Reset ──────────────
        const todayAfterHoursDate = getAfterHoursDate();
        if (todayAfterHoursDate && todayAfterHoursDate !== afterHoursResetFiredDateRef.current) {
          // Mark as fired so subsequent evaluations don't trigger again
          afterHoursResetFiredDateRef.current = todayAfterHoursDate;
          setDailyResetDate(todayAfterHoursDate);
          setDailyResetTimestamp(Date.now());
          console.log(`[ChinRiskControl] ⏰ Rule 4 FIRING: ${result.resetReason}`);
          onDailyResetRef.current(result.resetReason);
        }
        // If already fired today, do nothing — evaluation just shows blocked status
      } else {
        // ─── Rule 2 or 3: Emergency Reset ─────────────────
        const posSnapshot = JSON.stringify(
          positions.map((p) => `${p.symbolId}:${p.positionId}`).sort()
        );

        const isNewViolation =
          posSnapshot !== lastPositionSnapshotRef.current ||
          result.resetReason !== lastResetReasonRef.current;

        if (isNewViolation) {
          lastResetReasonRef.current = result.resetReason;
          lastPositionSnapshotRef.current = posSnapshot;
          onEmergencyReset(result.resetReason);
        }
      }
    } else if (!result.needsFullReset) {
      // No violation — clear tracking so next violation triggers a reset
      lastResetReasonRef.current = null;
      lastPositionSnapshotRef.current = '';
    }

    return result;
  }, [positions, buildSpreadInfo, symbolConfigs, activeSymbolIds, onEmergencyReset, rule4Enabled]);

  // ─── Periodic position polling ───────────────────────────

  useEffect(() => {
    if (!accountId) return;

    // Initial fetch
    fetchPositions();

    // Poll every 5 minutes (safety net — position count only; price/spread is realtime)
    const interval = setInterval(fetchPositions, POSITION_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [accountId, fetchPositions]);

  // ─── Re-evaluate on data changes ─────────────────────────

  useEffect(() => {
    runEvaluation();
  }, [positions, engineState?.lastTick, symbolConfigs, activeSymbolIds, runEvaluation]);

  // ─── canPlaceOrder helper ────────────────────────────────

  const canPlaceOrderFn = useCallback(
    (symbolId: number): { allowed: boolean; reason: string } => {
      if (!evaluation) return { allowed: false, reason: 'Risk evaluation not ready' };
      const check = evaluation.checks[symbolId];
      if (!check) return { allowed: false, reason: 'Symbol not in workspace' };
      return { allowed: check.allowed, reason: check.reason };
    },
    [evaluation],
  );

  return {
    evaluation,
    isLoading,
    refreshPositions: fetchPositions,
    canPlaceOrder: canPlaceOrderFn,
    positions,
    dailyResetDate,
    dailyResetTimestamp,
  };
}